import React, { useRef, useState } from 'react';
import { SalvButton, SalvTextField, Size } from '@salvapp/ui-components';
import { DropTargetMonitor, useDrag, useDrop, XYCoord } from 'react-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { DragItem } from '../../alert-status/AlertStatusRow';

interface Props {
  id: number;
  note: string;
  index: number;
  onChange: (value: string) => void;
  onRemove: () => void;
  moveField: (dragIndex: number, hoverIndex: number) => void;
}

const QuickStatusNoteRow = (props: Props) => {
  const { id, note, index, onChange, onRemove, moveField } = props;
  const [noteValue, setNoteValue] = useState(note);
  const [isEditing, setIsEditing] = useState(false);

  const ref = useRef<HTMLTableRowElement>(null);

  const [, drop] = useDrop({
    accept: 'QUICK_NOTE_ITEM',
    hover(item: DragItem, monitor: DropTargetMonitor<DragItem>) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveField(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'QUICK_NOTE_ITEM',
    item: { type: 'QUICK_NOTE_ITEM', id: id, index: index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  const onSave = () => {
    setIsEditing(false);
    onChange(noteValue);
  };

  return (
    <tr
      ref={ref}
      style={{ opacity }}
      onDragStart={(e) => {
        if (isEditing) {
          e.preventDefault();
        }
      }}
    >
      <td data-testid='grip-icon' width='28'>
        <FontAwesomeIcon icon={faGripVertical} className='text-muted cursor-grab' />
      </td>
      <td className='w-100 text-break'>
        <form>
          {isEditing ? (
            <div className='d-flex'>
              <SalvTextField
                className='w-100'
                size={Size.SMALL}
                ariaLabel={`note-${id}`}
                value={noteValue}
                onChange={setNoteValue}
              />
              <SalvButton
                type='submit'
                ariaLabel={`save-note-${id}`}
                label='Save'
                className='m-l-8 my-auto save-btn'
                variant='muted'
                size={Size.EXTRA_SMALL}
                onClick={onSave}
              />
            </div>
          ) : (
            <div className='d-flex'>
              <span className='text-s my-auto'>{note}</span>
              <div className='ms-auto d-flex flex-row flex-nowrap'>
                <SalvButton
                  label='Edit'
                  ariaLabel={`note-edit-${id}`}
                  variant='muted'
                  size={Size.EXTRA_SMALL}
                  onClick={() => {
                    setIsEditing(!isEditing);
                  }}
                />
                <SalvButton
                  ariaLabel={`note-remove-${id}`}
                  label='Remove'
                  className='m-l-8'
                  variant='danger-outline'
                  size={Size.EXTRA_SMALL}
                  onClick={onRemove}
                />
              </div>
            </div>
          )}
        </form>
      </td>
    </tr>
  );
};

export default QuickStatusNoteRow;
